import { useEffect, useState } from "react";
import { formatLocalDateTime } from "../../../utils/formatDate";
import AvisoMotoristaService from "../../../services/resource/avisoTelemetriaService";
import { reduzirNome } from "../../../utils/reduzirNome";
import { useSearchParams } from "react-router-dom";

const LIMITE_VELOCIDADE = 105;

function TabelaConceptPhone() {
    const [searchParams] = useSearchParams();

    const ano = Number(searchParams.get("ano"));
    const mes = Number(searchParams.get("mes"));
    const funcionarioId = Number(searchParams.get("funcionarioId"));

    const avisoMotoristaService = new AvisoMotoristaService();

    const [telemetrias, setTelemetrias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        avisoMotoristaService
            .listar({ funcionarioId: funcionarioId, ano, mes })
            .then((res) => setTelemetrias(res.data ?? []))
            .finally(() => setLoading(false));
    }, []);

    const filtrados = search.trim()
        ? telemetrias.filter((t) =>
            (t.descricao ?? "").toLowerCase().includes(search.toLowerCase()) ||
            (t.tipo ?? "").toLowerCase().includes(search.toLowerCase())
        )
        : telemetrias;

    const totalExcessos = filtrados.filter(
        (t) => (t.percentualVelocidade ?? 0) > LIMITE_VELOCIDADE
    ).length;

    return (
        <div className="min-h-screen flex flex-col gap-3 p-3">

            {/* ── Barra de busca + contador ── */}
            {!loading && telemetrias.length > 0 && (
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <i className="fa fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Filtrar por descrição ou tipo..."
                            className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                    {totalExcessos > 0 && (
                        <span className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-xs font-semibold text-red-600">
                            <i className="fa fa-exclamation-triangle" />
                            {totalExcessos} excesso{totalExcessos !== 1 ? "s" : ""}
                        </span>
                    )}
                </div>
            )}

            {/* ── Loading ── */}
            {loading && (
                <div className="flex flex-col items-center justify-center py-14 gap-2 text-gray-400">
                    <i className="fa fa-spinner fa-spin text-2xl text-teal-500" />
                    <p className="text-xs">Carregando avisos...</p>
                </div>
            )}

            {/* ── Empty ── */}
            {!loading && filtrados.length === 0 && (
                <div className="flex flex-col items-center justify-center py-14 gap-2 text-gray-400">
                    <i className="fa fa-bell-slash text-4xl opacity-20" />
                    <p className="text-xs">Nenhum aviso encontrado no período.</p>
                </div>
            )}

            {/* ── Cards ── */}
            {!loading && filtrados.length > 0 && (
                <>
                    <div className="space-y-2">
                        {filtrados.map((t, i) => (
                            <AvisoCard key={t.id ?? i} row={t} />
                        ))}
                    </div>

                    {/* Rodapé */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 flex flex-wrap justify-between items-center gap-2">
                        <span className="text-xs text-gray-500">
                            {filtrados.length} aviso{filtrados.length !== 1 ? "s" : ""}
                            {search && ` (filtrados de ${telemetrias.length})`}
                        </span>
                        {totalExcessos > 0 && (
                            <span className="text-xs text-red-600 font-semibold">
                                {totalExcessos} acima de {LIMITE_VELOCIDADE}%
                            </span>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default TabelaConceptPhone;

// ─── Card individual ──────────────────────────────────────────────────────────
function AvisoCard({ row }) {
    const excesso = (row.percentualVelocidade ?? 0) > LIMITE_VELOCIDADE;

    return (
        <div className={`bg-white rounded-xl border overflow-hidden ${excesso ? "border-red-200" : "border-gray-200"}`}>

            {/* Cabeçalho */}
            <div className={`flex items-center justify-between px-4 py-2.5 ${excesso ? "bg-red-50" : "bg-gray-50"} border-b border-gray-100`}>
                <div className="flex items-center gap-2 min-w-0">
                    <i className={`fa fa-bell text-sm flex-shrink-0 ${excesso ? "text-red-500" : "text-gray-400"}`} />
                    <p className="text-xs font-semibold text-gray-800 truncate">
                        {reduzirNome(row.funcionarioNome) ?? "—"}
                    </p>
                </div>
                <p className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                    {formatLocalDateTime(row.dataHora) ?? "—"}
                </p>
            </div>

            {/* Corpo */}
            <div className="px-4 py-3 space-y-2.5">

                {/* Descrição e tipo */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    <MiniInfo label="Descrição" value={row.descricao} wide />
                    <MiniInfo label="Tipo" value={row.tipo} />
                </div>

                {/* Velocidades */}
                <div className="grid grid-cols-3 gap-2">
                    <VelBox label="Vel. Via" value={row.velocidadeVia} alerta={false} />
                    <VelBox label="Vel. Veíc." value={row.velocidadeVeiculo} alerta={false} />
                    <VelBox
                        label="% Vel."
                        value={row.percentualVelocidade != null ? `${row.percentualVelocidade}%` : "—"}
                        alerta={excesso}
                    />
                </div>
            </div>
        </div>
    );
}

function MiniInfo({ label, value }) {
    return (
        <div>
            <p className="text-[9px] text-gray-400 uppercase">{label}</p>
            <p className="text-xs font-medium text-gray-700 break-words">{value ?? "—"}</p>
        </div>
    );
}

function VelBox({ label, value, alerta }) {
    return (
        <div className={`rounded-lg border px-2 py-1.5 text-center ${alerta ? "border-red-200 bg-red-50" : "border-gray-100 bg-gray-50"}`}>
            <p className="text-[9px] text-gray-400 uppercase">{label}</p>
            <p className={`text-xs font-bold ${alerta ? "text-red-600" : "text-gray-800"}`}>
                {value ?? "—"}
            </p>
        </div>
    );
}
